import { useEffect, useState, useCallback } from 'react';
import { cacheUtils } from '../utils/cacheUtils';

interface IconCacheHookResult {
  preloadIcons: (icons: IconDefinition[]) => Promise<void>;
  clearIconCache: () => void;
  getIconCacheStats: () => any;
  isIconCached: (name: string, type: string) => boolean;
}

interface IconDefinition {
  name: string;
  type: string;
  url?: string;
}

const useIconCache = (): IconCacheHookResult => {
  const [cacheInitialized, setCacheInitialized] = useState(false);

  useEffect(() => {
    const initializeIconCache = async () => {
      try {
        await preloadCriticalIcons();
        setCacheInitialized(true);
      } catch (error) {
        console.warn('Failed to initialize icon cache:', error);
        setCacheInitialized(true);
      }
    };

    initializeIconCache();
  }, []);

  const preloadIcons = useCallback(async (icons: IconDefinition[]) => {
    const promises = icons.map(async (icon) => {
      const cacheKey = `icon-${icon.type}-${icon.name}`;
      
      if (cacheUtils.icons.has(cacheKey)) {
        return;
      }

      try {
        if (icon.url || icon.type === 'custom') {
          const url = icon.url || `/assets/img/icons/${icon.name}.svg`;
          const response = await fetch(url);
          
          if (response.ok) {
            const content = await response.text();
            cacheUtils.icons.set(cacheKey, {
              svgContent: content,
              loaded: true,
              type: icon.type,
              name: icon.name,
            });
          }
        } else {
          cacheUtils.icons.set(cacheKey, {
            loaded: true,
            type: icon.type,
            name: icon.name,
          });
        }
      } catch (error) {
        console.warn(`Failed to preload icon: ${icon.name}`, error);
        cacheUtils.icons.set(cacheKey, {
          loaded: false,
          error: true,
          type: icon.type,
          name: icon.name,
        });
      }
    });

    await Promise.allSettled(promises);
  }, []);

  const clearIconCache = useCallback(() => {
    cacheUtils.icons.clear();
  }, []);

  const getIconCacheStats = useCallback(() => {
    return cacheUtils.icons.getStats();
  }, []);

  const isIconCached = useCallback((name: string, type: string) => {
    const cacheKey = `icon-${type}-${name}`;
    return cacheUtils.icons.has(cacheKey);
  }, []);

  return {
    preloadIcons,
    clearIconCache,
    getIconCacheStats,
    isIconCached,
  };
};

const preloadCriticalIcons = async () => {
  const criticalIcons: IconDefinition[] = [
    { name: 'user', type: 'feather' },
    { name: 'home', type: 'feather' },
    { name: 'settings', type: 'feather' },
    { name: 'search', type: 'feather' },
    { name: 'menu', type: 'feather' },
    { name: 'x', type: 'feather' },
    { name: 'chevron-down', type: 'feather' },
    { name: 'chevron-up', type: 'feather' },
    { name: 'chevron-left', type: 'feather' },
    { name: 'chevron-right', type: 'feather' },
    { name: 'plus', type: 'feather' },
    { name: 'minus', type: 'feather' },
    { name: 'edit', type: 'feather' },
    { name: 'trash-2', type: 'feather' },
    { name: 'save', type: 'feather' },
    { name: 'download', type: 'feather' },
    { name: 'upload', type: 'feather' },
    { name: 'mail', type: 'feather' },
    { name: 'phone', type: 'feather' },
    { name: 'calendar', type: 'feather' },
    { name: 'clock', type: 'feather' },
    { name: 'filter', type: 'feather' },
    { name: 'refresh-cw', type: 'feather' },
    { name: 'eye', type: 'feather' },
    { name: 'eye-off', type: 'feather' },
    { name: 'copy', type: 'feather' },
    { name: 'check', type: 'feather' },
    { name: 'alert-circle', type: 'feather' },
    { name: 'info', type: 'feather' },
    { name: 'help-circle', type: 'feather' },

    { name: 'users', type: 'fontawesome' },
    { name: 'building', type: 'fontawesome' },
    { name: 'handshake', type: 'fontawesome' },
    { name: 'chart-line', type: 'fontawesome' },
    { name: 'briefcase', type: 'fontawesome' },
    { name: 'dollar-sign', type: 'fontawesome' },

    { name: 'pdf-02', type: 'custom', url: '/assets/img/icons/pdf-02.svg' },
    { name: 'word', type: 'custom', url: '/assets/img/icons/word.png' },
    { name: 'xls', type: 'custom', url: '/assets/img/icons/xls.svg' },
    { name: 'image', type: 'custom', url: '/assets/img/icons/image.svg' },
    { name: 'folder', type: 'custom', url: '/assets/img/icons/folder.svg' },
    { name: 'file', type: 'custom', url: '/assets/img/icons/file.svg' },
  ];

  const promises = criticalIcons.map(async (icon) => {
    const cacheKey = `icon-${icon.type}-${icon.name}`;
    
    if (cacheUtils.icons.has(cacheKey)) {
      return;
    }

    try {
      if (icon.url || icon.type === 'custom') {
        const url = icon.url || `/assets/img/icons/${icon.name}.svg`;
        const response = await fetch(url);
        
        if (response.ok) {
          const content = await response.text();
          cacheUtils.icons.set(cacheKey, {
            svgContent: content,
            loaded: true,
            type: icon.type,
            name: icon.name,
          });
        }
      } else {
        cacheUtils.icons.set(cacheKey, {
          loaded: true,
          type: icon.type,
          name: icon.name,
        });
      }
    } catch (error) {
      console.warn(`Failed to preload icon: ${icon.name}`, error);
      cacheUtils.icons.set(cacheKey, {
        loaded: false,
        error: true,
        type: icon.type,
        name: icon.name,
      });
    }
  });

  await Promise.allSettled(promises);
};

export { preloadCriticalIcons };
export default useIconCache; 